'use client';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { register as registerUser } from '@/store/slices/authSlice';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/store/store';
import { useState, useEffect } from 'react';
import { useSession, signIn } from 'next-auth/react';
import toast from 'react-hot-toast';
import Link from 'next/link';
import Image from 'next/image';
import Consent from '../Consent';

type SignUpForm = {
  displayName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export default function SignUp() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<SignUpForm>({
    mode: 'onTouched',
  });
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const { data: session, status } = useSession();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingData, setPendingData] = useState<SignUpForm | null>(null);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (status === 'authenticated' && session && !pendingData) {
      router.push('/dashboard');
    }
  }, [session, status, router, pendingData]);

 const onSubmit = async (data: SignUpForm) => {
  if (data.password !== data.confirmPassword) {
    setSubmitError('Passwords do not match');
    return;
  }
  setPendingData(data);
  setShowModal(true);
};

const handleConsentAccept = async () => {
  if (!pendingData) return;
  setLoading(true);
  setSubmitError('');
  try {
    await toast.promise(
      dispatch(
        registerUser({
          email: pendingData.email,
          password: pendingData.password,
          displayName: pendingData.displayName,
          username: pendingData.username,
          consent: true,
        })
      ).unwrap(),
      {
        loading: 'Creating your account...',
        success: 'Account created successfully!',
        error: (err) => err || 'Registration failed. Please try again.',
      }
    );

    // Auto sign in after registration to create NextAuth session
    const result = await signIn('credentials', {
      email: pendingData.email,
      password: pendingData.password,
      redirect: false,
    });

    if (result?.ok) {
      router.push('/dashboard');
    } else {
      router.push('/auth/login');
    }
  } catch (err) {
    console.error('Registration error:', err);
  } finally {
    setLoading(false);
  }
};

  // Show loading while checking authentication
  if (status === 'loading') {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-slate-50 via-[#d8e8ee] to-[#bfd3db]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004C73] mx-auto"></div>
          <p className="mt-4 text-[#004C73]">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render the form if user is authenticated (will redirect)
  if (status === 'authenticated') {
    return null;
  }

  return (
    <div
          className="min-h-screen w-full flex flex-col items-center justify-center 
            bg-gradient-to-b from-slate-50 via-[#d8e8ee] to-[#bfd3db] 
            relative overflow-auto py-8"
    >
      {/* Back to Home Icon */}
      <Link
        href="/"
        className="absolute top-6 left-6 p-2 hover:bg-white/20 rounded-lg transition-all duration-200 group z-20"
        title="Back to Home"
      >
        <Image
          src="/message-icon.png"
          alt="Back to Home"
          width={48}
          height={48}
          className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 group-hover:scale-105 transition-transform duration-200"
        />
      </Link>
      {/* Background Elements */}
      {/* <div className="absolute inset-0 opacity-30 pointer-events-none select-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-[#32738c] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#32738c] rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div> */}
      <div className="relative z-10 flex flex-col md:flex-row w-full max-w-4xl items-center justify-center">
        {/* Left Section (Welcome) - only on md+ screens */}
        {/* <div className="hidden md:flex flex-1 flex-col justify-center items-center">
          <div className="flex flex-col gap-2 ps-5">
            <span className="text-5xl md:text-4xl lg:text-5xl xxl:text-6xl text-[#F2A74B] font-extrabold md:font-light lg:font-extrabold sm:font-light">
              Welcome to Lingwell
            </span>
            <span className="text-lg sm:text-sm md:text-base lg:text-xl text-[#333333]">
              Consequat adipisicing ea do labore irure adipisicing occaecat
              cupidatat
            </span>
            <div className="flex gap-4 mt-8 text-3xl">
              <span>☕</span>
              <span>☀️</span>
              <span>💡</span>
            </div>
          </div>
        </div> */}
        {/* Right Section (Solid Card Sign-Up) */}
        <div className="flex flex-1 justify-center items-center w-full max-w-md p-4 sm:p-6">
          <form
            onSubmit={handleSubmit(onSubmit)}
           className="w-full bg-white rounded-2xl sm:rounded-3xl shadow-2xl px-4 sm:px-5 md:px-8 py-6 sm:py-8 md:py-10 flex flex-col gap-5 sm:gap-6 md:gap-7"
          >
            <h2 className="text-2xl sm:text-3xl text-[#004C73] text-center mb-2 tracking-tight">
              New User
            </h2>

            <div className="flex flex-col gap-3 sm:gap-4">
              <div>
                <label className="text-[#024d78] text-base sm:text-lg font-light">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('displayName', {
                    required: 'Name is required',
                    maxLength: { value: 100, message: 'Name must be 100 characters or less' },
                  })}
                  type="text"
                  maxLength={100}
                  placeholder="Enter your full name"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border ${
                    errors.displayName ? 'border-red-500' : 'border-[#e0e7ef]'
                  } focus:outline-none focus:ring-2 focus:ring-[#024d78] text-base sm:text-lg`}
                  onClick={() => clearErrors('displayName')}
                />
                {errors.displayName && (
                  <p className="text-red-500 text-sm mt-1">{errors.displayName.message}</p>
                )}
              </div>
              <div>
                <label className="text-[#024d78] text-base sm:text-lg font-light">
                  User Name{' '}
                  <span className="text-red-500 text-base sm:text-lg font-light">
                    *
                  </span>
                </label>
                <input
                  {...register('username', {
                    required: 'Username is required',
                    minLength: { value: 3, message: 'Username must be at least 3 characters' },
                    maxLength: { value: 50, message: 'Username must be 50 characters or less' },
                    pattern: {
                      value: /^(?=.*[a-zA-Z])[a-zA-Z0-9_-]+$/,
                      message: 'Use letters, numbers, underscores, or hyphens only (no spaces)',
                    },
                  })}
                  type="text"
                  maxLength={50}
                  placeholder="Choose a username (no spaces)"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border ${
                    errors.username ? 'border-red-500' : 'border-[#e0e7ef]'
                  } focus:outline-none focus:ring-2 focus:ring-[#024d78] text-base sm:text-lg`}
                  onClick={() => clearErrors('username')}
                />
                {errors.username && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.username.message}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[#024d78] text-base sm:text-lg font-light">
                  Email{' '}
                  <span className="text-red-500 text-base sm:text-lg font-light">
                    *
                  </span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Invalid email address',
                    },
                  })}
                  maxLength={50}
                  type="email"
                  placeholder="example.email@gmail.com"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border ${
                    errors.email ? 'border-red-500' : 'border-[#e0e7ef]'
                  } focus:outline-none focus:ring-2 focus:ring-[#024d78] text-base sm:text-lg`}
                  onClick={() => clearErrors('email')}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[#024d78] text-base sm:text-lg font-light">
                  Password{' '}
                  <span className="text-red-500 text-base sm:text-lg font-light">
                    *
                  </span>
                </label>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                      maxLength: {
                        value: 20,
                        message: 'Password must be 20 characters or less',
                      },
                    })}
                    maxLength={20}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter at least 8+ characters"
                    className={`w-full px-3 sm:px-4 pr-10 sm:pr-12 py-2.5 sm:py-3 rounded-lg border ${
                      errors.password ? 'border-red-500' : 'border-[#e0e7ef]'
                    } focus:outline-none focus:ring-2 focus:ring-[#024d78] text-base sm:text-lg`}
                    onClick={() => clearErrors('password')}
                  />
                  {/* Eye icon button */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#004C73] focus:outline-none"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      // Eye open icon (you can replace with SVG or emoji)
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    ) : (
                      // Eye closed icon
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-eye-off-icon lucide-eye-off"
                      >
                        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                        <path d="m2 2 20 20" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[#024d78] text-base sm:text-lg font-light">
                  Re-Enter Password{' '}
                  <span className="text-red-500 text-base sm:text-lg font-light">
                    *
                  </span>
                </label>
                <div className="relative">
                  <input
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      maxLength: {
                        value: 20,
                        message: 'Password must be 20 characters or less',
                      },
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-Enter Password"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    maxLength={20}
                    className={`w-full px-3 sm:px-4 pr-10 sm:pr-12 py-2.5 sm:py-3 rounded-lg border ${
                      errors.confirmPassword
                        ? 'border-red-500'
                        : 'border-[#e0e7ef]'
                    } focus:outline-none focus:ring-2 focus:ring-[#024d78] text-base sm:text-lg`}
                    onClick={() => clearErrors('confirmPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-[#004C73] focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      // Eye open icon (you can replace with SVG or emoji)
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    ) : (
                      // Eye closed icon
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-eye-off-icon lucide-eye-off"
                      >
                        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                        <path d="m2 2 20 20" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
              <label className="flex items-center gap-2 text-sm sm:text-base text-[#024d78]">
                <input type="checkbox" className="accent-[#024d78] w-4 h-4" />{' '}
                Remember me
              </label>
              {/* <Link
                href="/forgot-password"
                className="text-[#024d78] hover:underline text-xs sm:text-sm"
              >
                Forgot password?
              </Link> */}
            </div>

            {/* <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 border border-gray-200 rounded-lg p-3 sm:p-4 bg-[#F8FAFC] shadow-sm">
              <div className="flex items-start gap-2 text-xs sm:text-sm text-[#004C73] leading-snug">
                <input
                  type="checkbox"
                  className="mt-1 accent-[#024d78] w-4 h-4 flex-shrink-0"
                  checked={isConsentChecked}
                  onChange={(e) => setIsConsentChecked(e.target.checked)}
                />

                <span className="mt-0.5">
                  I have read and agree to the{' '}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModal(true);
                    }}
                    className="underline text-[#024d78] hover:text-[#22566c] transition cursor-pointer"
                  >
                    Lingwell General Use Consent Form
                  </span>
                  .
                </span>
              </div>
            </div> */}

            {submitError && (
              <div className="text-red-500 text-center text-sm sm:text-base">
                {submitError}
              </div>
            )}

            <button
              type="submit"
            className="w-full text-center px-6 py-2 sm:py-1 
              text-base sm:text-base md:text-lg rounded-full font-light 
              text-white shadow-md transition-transform hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(180deg, #67C17E 0%, #3FA057 100%)',
              }}
              disabled={loading}
            >
              {loading ? 'Signing up...' : 'Create Account'}
            </button>

            <div className="text-center text-sm sm:text-base text-[#004C73]">
              {' '}
              <Link className="hover:underline" href="/auth/login">
                Or Sign In
              </Link>
            </div>
            {/* <div className="flex justify-center gap-4">
            <button
              type="button"
              className="bg-[#f2fafc] border border-[#e0e7ef] rounded-full p-2 w-12 h-12 flex items-center justify-center text-2xl"
            >
              <span>🟢</span>
            </button>
            <button
              type="button"
              className="bg-[#f2fafc] border border-[#e0e7ef] rounded-full p-2 w-12 h-12 flex items-center justify-center text-2xl"
            >
              <span>🔵</span>
            </button>
            <button
              type="button"
              className="bg-[#f2fafc] border border-[#e0e7ef] rounded-full p-2 w-12 h-12 flex items-center justify-center text-2xl"
            >
              <span>⚪</span>
            </button>
          </div> */}
          </form>
        </div>
      </div>
      {/* Modal */}
      {showModal && (
        <Consent
          setShowModal={setShowModal}
          onAccept={handleConsentAccept}
        />
      )}
    </div>
  );
}
